import { useContext, useMemo, useCallback } from 'react';
import { WizardContext } from './WizardContext';
import { WIZARD_ACTIONS } from './wizardReducer';
import {
  selectCanContinue,
  selectCurrentStep,
  selectNextStep,
  selectProductCount,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/selectors';

import { createEmptyWizardProduct } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

export const useWizard = () => {
  const context = useContext(WizardContext);

  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }

  const { state, dispatch, callbacks } = context;

  // Memoized actions
  const actions = useMemo(
    () => ({
      setWizardFormId: (wizardFormId) => {
        dispatch({ type: WIZARD_ACTIONS.SET_WIZARD_FORM_ID, wizardFormId });
      },

      setAnswer: (field, value) => {
        dispatch({ type: WIZARD_ACTIONS.SET_ANSWER, field, value });
      },

      mergeAnswers: (payload) => {
        dispatch({ type: WIZARD_ACTIONS.MERGE_ANSWERS, payload });
      },

      resetAnswers: () => {
        dispatch({ type: WIZARD_ACTIONS.RESET_ANSWERS });
      },

      openModal: (modal) =>
        dispatch({
          type: WIZARD_ACTIONS.OPEN_MODAL,
          modalName: modal,
        }),

      closeModal: (modal) =>
        dispatch({
          type: WIZARD_ACTIONS.CLOSE_MODAL,
          modalName: modal,
        }),

      // Product management
      setProducts: (products) => {
        dispatch({ type: WIZARD_ACTIONS.SET_PRODUCTS, products });
      },

      addProduct: (product) => {
        dispatch({
          type: WIZARD_ACTIONS.ADD_PRODUCT,
          product: product || createEmptyWizardProduct(),
        });
      },

      updateProduct: (key, field, value) => {
        dispatch({ type: WIZARD_ACTIONS.UPDATE_PRODUCT, key, field, value });
      },

      removeProduct: (key) => {
        dispatch({ type: WIZARD_ACTIONS.REMOVE_PRODUCT, key });
      },

      // Date
      setDate: (date) => {
        dispatch({ type: WIZARD_ACTIONS.SET_DATE, date });
      },

      // Navigation
      goNext: (step) => {
        dispatch({ type: WIZARD_ACTIONS.GO_NEXT, step });
      },

      setGridLoading: (value) => {
        dispatch({ type: WIZARD_ACTIONS.SET_GRID_LOADING, value });
      },

      setBypassed: (value) => {
        dispatch({ type: WIZARD_ACTIONS.SET_BYPASSED, value });
      },

      goBack: () => {
        dispatch({ type: WIZARD_ACTIONS.GO_BACK });
      },

      goToStep: (step) => {
        dispatch({ type: WIZARD_ACTIONS.GO_TO_STEP, step });
      },

      // UI state
      setSubmitting: (value) => {
        dispatch({ type: WIZARD_ACTIONS.SET_SUBMITTING, value });
      },

      setError: (error) => {
        dispatch({ type: WIZARD_ACTIONS.SET_ERROR, error });
      },

      restoreState: (payload) => {
        dispatch({ type: WIZARD_ACTIONS.RESTORE_STATE, payload });
      },

      // Reset
      reset: () => {
        dispatch({ type: WIZARD_ACTIONS.RESET });
      },
    }),
    [dispatch]
  );

  // Computed values using selectors
  const currentStep = selectCurrentStep(state);
  const canContinue = selectCanContinue(state, currentStep);
  const nextStepKey = selectNextStep(state, currentStep);
  const productCount = selectProductCount(state);

  // Navigation helpers
  const handleContinue = useCallback(() => {
    if (canContinue && nextStepKey) {
      actions.goNext(nextStepKey);
    }
  }, [canContinue, nextStepKey, actions]);

  const handleBack = useCallback(() => {
    actions.goBack();
  }, [actions]);

  const handleGoToStep = useCallback(
    (targetStep) => {
      actions.goToStep(targetStep);
    },
    [actions]
  );

  const handleClose = useCallback(() => {
    actions.reset();
    callbacks.onClose?.();
  }, [actions, callbacks]);

  const isModalOpen = useCallback(
    (modalName) => {
      return !!state.activeModals[modalName];
    },
    [state.activeModals]
  );

  return {
    // State
    state,

    // Actions
    actions,

    // Computed
    currentStep,
    canContinue,
    nextStepKey,
    productCount,

    // Navigation handlers
    handleContinue,
    handleBack,
    handleGoToStep,
    handleClose,

    openModal: actions.openModal,
    closeModal: actions.closeModal,

    isModalOpen,
  };
};

export default useWizard;
